import { HttpClient } from '@wix/http-client';
import type {
  AppData,
  AppDataComponent,
  DocumentServicesObject,
} from '@wix/document-services-types';
import { CompRef } from '@wix/document-services-types';
import type {
  AppInstallOption,
  IPublicManifestAPI,
  PlatformApiMethods,
  PluginInstallationParams,
  UniversalSlotPointer,
} from '@wix/editor-platform-host-integration-apis';
import type { PluginPlacement } from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/types';
import {
  ComponentType,
  WidgetPluginComponentData,
} from '@wix/ambassador-app-service-webapp/types';
import { SlotData, WidgetPointer } from '../../../../../types/widgetPlugins';
import { findIndex } from 'lodash';
import { createPluginHostApi } from '../../../plugins/pluginHostAPI';
import {
  canOpenModal as canOpenReplacePluginModal,
  type ReplacePluginModalProps,
  type Plugin,
  type ReplacementOption,
} from '@wix/editor-platform-shared-ui';
import { IExperimentsApiAdapter } from '../../../../utils/experimentsApiAdapter';
import { SlotPointer } from '../../../plugins/slotPointer';
import { HEADER_SYSTEM_SLOT } from '../../../plugins/constants';

// TODO: move methods to SlotsService and PluginService
interface SlotsResponse {
  slots: SlotData[];
}

const getSlotComponentRef = (
  documentServicesAPI: DocumentServicesObject,
  slot: SlotData,
): CompRef | null => {
  const VIRTUAL_SLOT_ID_DIVIDER = '_vs_';
  const isBlocksSlot = slot?.scope?.componentIds?.length;

  if (isBlocksSlot) {
    const [hostWidgetId] = slot!.scope!.componentIds!;
    const hostWidgetCompRef =
      documentServicesAPI.components.get.byId(hostWidgetId);

    if (!hostWidgetCompRef) {
      return null;
    }

    const widgetCompSlots =
      documentServicesAPI.components.slots.getWidgetSlots(hostWidgetCompRef);

    if (!widgetCompSlots) {
      return null;
    }

    const slotComponent = widgetCompSlots?.find(
      ({ role }) => role === slot.slotId,
    );

    if (!slotComponent) {
      return null;
    }

    return slotComponent.compRef as CompRef;
  } else {
    const widgetCompRef = documentServicesAPI.components.get.byId(
      slot.componentId,
    );

    if (!widgetCompRef) {
      return null;
    }

    return {
      id: `${widgetCompRef.id}${VIRTUAL_SLOT_ID_DIVIDER}${slot.slotId}`,
      type: widgetCompRef.type,
    };
  }
};

const buildSlotPointerForRow = (
  documentServicesAPI: DocumentServicesObject,
  manifestAPI: IPublicManifestAPI,
  experimentsAPI: IExperimentsApiAdapter,
  slot: SlotData,
): { input: CompRef | UniversalSlotPointer; pointer: SlotPointer } | null => {
  if (slot.id) {
    const hostCompRef = documentServicesAPI.components.get.byId(
      slot.componentId,
    );

    if (!hostCompRef) {
      return null;
    }

    const input: UniversalSlotPointer = {
      hostCompRef,
      slotId: slot.id,
    };

    return {
      input,
      pointer: SlotPointer.create(
        documentServicesAPI,
        manifestAPI,
        experimentsAPI,
        input,
      ),
    };
  }

  const slotComponentRef = getSlotComponentRef(documentServicesAPI, slot);
  if (!slotComponentRef) {
    return null;
  }
  return {
    input: slotComponentRef,
    pointer: new SlotPointer({ slotRef: slotComponentRef }),
  };
};

const getAvailableSlots = async (
  documentServicesAPI: DocumentServicesObject,
  pluginPlacements: PluginPlacement[] = [],
  placementsSlotIds: string[] = [],
): Promise<SlotData[]> => {
  const META_SITE_APPLICATION_ID = '-666';
  const getAppToken = () =>
    documentServicesAPI.platform.getAppDataByApplicationId(
      META_SITE_APPLICATION_ID,
    )?.instance;

  const httpClient = new HttpClient({
    baseURL: '/_serverless/editor-platform-widget-plugins/',
    getAppToken,
  });

  if (!pluginPlacements?.length && !placementsSlotIds?.length) {
    return [];
  }

  const queryParams = new URLSearchParams();
  if (pluginPlacements?.length) {
    queryParams.append('placements', JSON.stringify(pluginPlacements));
  }
  if (placementsSlotIds?.length) {
    queryParams.append('slotIds', placementsSlotIds.join(','));
  }
  queryParams.append('withSlotContent', 'true');
  const res = await httpClient.get<SlotsResponse>(
    `/slots?${queryParams.toString()}`,
  );

  return res.data.slots;
};

function sortSlotsByPluginPlacements(
  slots: SlotData[],
  placements: PluginPlacement[],
) {
  const sortedSlots = [...slots].sort(function (a, b) {
    return (
      findIndex(placements, { widgetId: a.widgetId, slotId: a.slotId }) -
      findIndex(placements, { widgetId: b.widgetId, slotId: b.slotId })
    );
  });
  return sortedSlots;
}

type WidgetPluginComponentDataWithComponentId = WidgetPluginComponentData & {
  componentId: string;
  placementsSlotIds?: string[];
};

const getPluginsForAutoPopulation = (
  components: AppDataComponent[],
  slotIdPredicate: (slotId: string) => boolean = () => true,
): WidgetPluginComponentDataWithComponentId[] => {
  const isPluginCompType = (comp: AppDataComponent) =>
    comp.type === ComponentType.WIDGET_PLUGIN;

  // WidgetPluginComponentData has an explicit type as AppDataComponent['data'] doesn't provide a correct type for all possible component types
  return (
    components
      ?.filter(isPluginCompType)
      .flatMap((widget) => {
        const plugin = {
          ...widget.data,
          componentId: widget.componentId as string,
        } as WidgetPluginComponentDataWithComponentId;

        if (!plugin.placementsSlotIds) {
          return [plugin];
        }

        const placementsSlotIds =
          plugin.placementsSlotIds.filter(slotIdPredicate);

        // The predicate emptied a plugin that previously targeted slots (e.g. a
        // header-only plugin in the generic flow, or a generic plugin in the
        // header flow). Drop it so the two flows don't both install it.
        if (
          plugin.placementsSlotIds.length > 0 &&
          placementsSlotIds.length === 0
        ) {
          return [];
        }

        return [{ ...plugin, placementsSlotIds }];
      })
      .filter((plugin) => plugin.installation?.base?.autoAdd) ?? []
  );
};

const confirmPluginReplace = async (
  plugins: Plugin[],
  slots: SlotData[],
  platformAPI: PlatformApiMethods,
): Promise<ReplacementOption[]> => {
  const panelName = 'tpaPanels.confirmation.pluginReplace';
  const languageCode = window.editorModel?.languageCode;

  const closePanel = () => {
    platformAPI.editor.closePanel({} as AppData, panelName);
  };

  return new Promise((resolve) => {
    const onCancel = () => {
      closePanel();
      resolve([]);
    };

    const onReplace = (replacementOptions: ReplacementOption[]) => {
      closePanel();
      resolve(replacementOptions);
    };

    const onHelp = () => {
      const helpArticleId = '68ab8d2f-c2d2-4601-95d0-52d25ba1c24d';
      const url = new URL(
        `https://wix.wixanswers.com/apps/widget/v1/wix/53f85d67-f8b0-41ca-9dc8-5a16390cc74f/view/${languageCode}/article/${helpArticleId}`,
      );

      window.open(url, '_blank');
    };

    platformAPI.editor.openPanel(
      {} as AppData,
      panelName,
      {
        width: '720px',
        token: panelName,
        plugins,
        slots,
        onCancel,
        onReplace,
        onHelp,
        onClose: onCancel,
        cancelActionType: 'skip',
        origin: 'installation',
        locale: languageCode,
      },
      true,
    );
  });
};

type SlotDataWithWidgetName = SlotData & { widgetName: string };

const getAllPluginsInstallationParams = async (
  documentServicesAPI: DocumentServicesObject,
  manifestAPI: IPublicManifestAPI,
  appDefinitionId: string,
  components: AppDataComponent[],
  platformAPI: PlatformApiMethods,
  experimentsAPI: IExperimentsApiAdapter,
  appsOptions?: AppInstallOption,
  slotIdPredicate?: (slotId: string) => boolean,
): Promise<PluginInstallationParams[]> => {
  // Header plugins are auto-populated separately, so by default we exclude the
  // header slot from the generic plugin installation flow.
  const isNonHeaderSlot = (slotId: string) => slotId !== HEADER_SYSTEM_SLOT.id;
  const predicate = slotIdPredicate ?? isNonHeaderSlot;

  const widgetPlugins = getPluginsForAutoPopulation(components, predicate);

  if (widgetPlugins?.length === 0) {
    return [];
  }

  const pluginInstallationParams: PluginInstallationParams[] = [];

  const pluginsNeedingReplacement: Plugin[] = [];
  const replacementSlots: SlotDataWithWidgetName[] = [];

  const getPluginInstallationParam = (
    slot: SlotData,
    widgetRef: WidgetPointer,
  ): PluginInstallationParams | null => {
    const built = buildSlotPointerForRow(
      documentServicesAPI,
      manifestAPI,
      experimentsAPI,
      slot,
    );
    if (!built) {
      return null;
    }
    return {
      slotComponentRef: built.input,
      widgetRef,
      pageId: slot.pageId,
    };
  };

  const findFirstFreeSlot = (slots: SlotData[]): SlotData | undefined => {
    return slots.find((slot) => {
      const built = buildSlotPointerForRow(
        documentServicesAPI,
        manifestAPI,
        experimentsAPI,
        slot,
      );
      if (!built) {
        return false;
      }
      const api = createPluginHostApi(documentServicesAPI, experimentsAPI);

      try {
        const slotData = api.getWidgetSlot(built.pointer, {
          includePrivate: false,
        });

        return !slotData?.pluginInfo;
      } catch {
        return false;
      }
    });
  };

  for (const plugin of widgetPlugins) {
    const availableSlots = await getAvailableSlots(
      documentServicesAPI,
      plugin.placements,
      plugin.placementsSlotIds,
    );

    if (!availableSlots.length) {
      continue;
    }

    replacementSlots.push(
      ...availableSlots.map((slot) => ({
        ...slot,
        widgetName: slot.pageTitle,
      })),
    );

    const slots = sortSlotsByPluginPlacements(
      availableSlots,
      plugin.placements || [],
    );

    const firstFreeSlot = findFirstFreeSlot(slots);

    if (!firstFreeSlot) {
      pluginsNeedingReplacement.push({
        id: plugin.componentId,
        name: plugin.marketData?.name!,
        icon: plugin.marketData?.logoUrl,
        // @ts-expect-error
        placements: plugin.placements!,
      });

      continue;
    }

    const params = getPluginInstallationParam(firstFreeSlot, {
      appDefinitionId,
      widgetId: plugin.referenceComponentId!,
    });
    if (params) {
      pluginInstallationParams.push(params);
    }
  }

  const shouldOpenReplacePluginModal = [
    canOpenReplacePluginModal(
      pluginsNeedingReplacement as ReplacePluginModalProps['plugins'],
      replacementSlots as ReplacePluginModalProps['slots'],
    ),
    appsOptions?.pluginsAutoPopulate?.isSilent !== true,
  ].every(Boolean);

  if (!shouldOpenReplacePluginModal) {
    return pluginInstallationParams;
  }

  const replacements = await confirmPluginReplace(
    pluginsNeedingReplacement,
    replacementSlots,
    platformAPI,
  );

  replacements.forEach((replacement) => {
    const slot = replacementSlots.find(
      (findSlot) =>
        findSlot.slotId === replacement.placement.slotId &&
        findSlot.widgetId === replacement.placement.widgetId,
    )!;

    const plugin = widgetPlugins.find(
      (findPlugin) => findPlugin.componentId === replacement.pluginId,
    )!;

    const params = getPluginInstallationParam(slot, {
      appDefinitionId,
      widgetId: plugin.referenceComponentId!,
    });
    if (params) {
      pluginInstallationParams.push(params);
    }
  });

  return pluginInstallationParams;
};

export { getAllPluginsInstallationParams, getPluginsForAutoPopulation };
